import { useState, useMemo, useCallback } from 'react';
import {
  addMonths,
  subMonths,
  startOfMonth,
  startOfWeek,
  endOfMonth,
  endOfWeek,
  format,
  parseISO,
  isSameDay,
  differenceInWeeks,
} from 'date-fns';
import { ChevronLeft, ChevronRight, Loader2, Clock, Plus, X } from 'lucide-react';
import {
  useAvailability,
  usePTO,
  useCreateAvailability,
  useDeleteAvailability,
  useCreatePTO,
  useDeletePTO,
  useCreateDateAvailability,
  useDeleteDateAvailability,
  type AvailabilityRule,
  type DateSpecificAvailability,
  type PTODay,
} from '../hooks/useAvailability';
import { StandardWeekRow } from './StandardWeekRow';
import { MonthGrid } from './MonthGrid';
import { DayEditorPopover } from './DayEditorPopover';

const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const STANDARD_HOURS = { start: '08:00', end: '17:00' };

interface AvailabilityCalendarProps {
  personId: string;
  personName: string;
}

export function AvailabilityCalendar({ personId, personName }: AvailabilityCalendarProps) {
  // Current month being viewed
  const [currentMonth, setCurrentMonth] = useState(() => startOfMonth(new Date()));

  // State for editing a standard week day
  const [editingStandardDay, setEditingStandardDay] = useState<number | null>(null);
  const [standardDayForm, setStandardDayForm] = useState({
    startTime: '08:00',
    endTime: '17:00',
  });

  // State for day editor popover
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [popoverPosition, setPopoverPosition] = useState({ x: 0, y: 0 });

  // Data hooks
  const { data: availabilityData, isLoading: isLoadingAvailability } = useAvailability(personId);
  const { data: pto = [], isLoading: isLoadingPTO } = usePTO(personId);

  // Extract recurring and date-specific availability
  const recurring = availabilityData?.recurring ?? [];
  const dateSpecific = availabilityData?.dateSpecific ?? [];

  // Mutations
  const createAvailabilityMutation = useCreateAvailability();
  const deleteAvailabilityMutation = useDeleteAvailability(personId);
  const createPTOMutation = useCreatePTO();
  const deletePTOMutation = useDeletePTO(personId);
  const createDateAvailabilityMutation = useCreateDateAvailability();
  const deleteeDateAvailabilityMutation = useDeleteDateAvailability(personId);

  const isLoading = isLoadingAvailability || isLoadingPTO;
  const isSaving =
    createAvailabilityMutation.isPending ||
    deleteAvailabilityMutation.isPending ||
    createPTOMutation.isPending ||
    deletePTOMutation.isPending ||
    createDateAvailabilityMutation.isPending ||
    deleteeDateAvailabilityMutation.isPending;

  // Calculate the date range for the month grid
  const { gridStartDate, weeksToShow } = useMemo(() => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const gridStart = startOfWeek(monthStart); // Start of week containing the 1st
    const gridEnd = endOfWeek(monthEnd); // End of week containing the last day

    // Calculate weeks to show (typically 5, sometimes 6)
    const weeks = differenceInWeeks(gridEnd, gridStart) + 1;

    return {
      gridStartDate: gridStart,
      weeksToShow: weeks,
    };
  }, [currentMonth]);

  // Navigate months
  const goToPreviousMonth = () => setCurrentMonth((prev) => subMonths(prev, 1));
  const goToNextMonth = () => setCurrentMonth((prev) => addMonths(prev, 1));
  const goToToday = () => setCurrentMonth(startOfMonth(new Date()));

  // Get rule for a day of week (for standard week editing)
  const getRuleForDayOfWeek = (dayOfWeek: number): AvailabilityRule | null => {
    return recurring.find((rule) => rule.dayOfWeek === dayOfWeek) ?? null;
  };

  // Handle standard week day edit
  const handleEditStandardDay = (dayOfWeek: number) => {
    const existingRule = getRuleForDayOfWeek(dayOfWeek);
    setStandardDayForm({
      startTime: existingRule?.startTime ?? '08:00',
      endTime: existingRule?.endTime ?? '17:00',
    });
    setEditingStandardDay(dayOfWeek);
  };

  // Save or update standard day rule
  const handleSaveStandardDay = () => {
    if (editingStandardDay === null) return;

    const existingRule = getRuleForDayOfWeek(editingStandardDay);

    if (existingRule) {
      // Delete existing rule first, then create new one
      // (API doesn't support updating, so we delete and recreate)
      deleteAvailabilityMutation.mutate(existingRule.id, {
        onSuccess: () => {
          createAvailabilityMutation.mutate({
            personId,
            dayOfWeek: editingStandardDay,
            startTime: standardDayForm.startTime,
            endTime: standardDayForm.endTime,
            effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
          });
        },
      });
    } else {
      createAvailabilityMutation.mutate({
        personId,
        dayOfWeek: editingStandardDay,
        startTime: standardDayForm.startTime,
        endTime: standardDayForm.endTime,
        effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
      });
    }

    setEditingStandardDay(null);
  };

  // Delete standard day rule (set day off)
  const handleDeleteStandardDay = () => {
    if (editingStandardDay === null) return;

    const existingRule = getRuleForDayOfWeek(editingStandardDay);
    if (existingRule) {
      deleteAvailabilityMutation.mutate(existingRule.id);
    }
    setEditingStandardDay(null);
  };

  // Set standard hours for all weekdays
  const handleSetStandardHours = () => {
    for (let day = 1; day <= 5; day++) {
      const existingRule = recurring.find((r) => r.dayOfWeek === day);
      if (!existingRule) {
        createAvailabilityMutation.mutate({
          personId,
          dayOfWeek: day,
          startTime: STANDARD_HOURS.start,
          endTime: STANDARD_HOURS.end,
          effectiveFrom: format(new Date(), 'yyyy-MM-dd'),
        });
      }
    }
  };

  // Handle date selection from month grid
  const handleSelectDate = useCallback((date: Date, event: React.MouseEvent) => {
    // Get click position for popover
    const position = { x: event.clientX, y: event.clientY };
    setPopoverPosition(position);
    setSelectedDate(date);
  }, []);

  // Get data for selected date
  const selectedDateData = useMemo(() => {
    if (!selectedDate) return null;

    const dayOfWeek = selectedDate.getDay();
    const standardRule = getRuleForDayOfWeek(dayOfWeek);
    const dateStr = format(selectedDate, 'yyyy-MM-dd');

    const override = dateSpecific.find((d) => d.date === dateStr) ?? null;
    const ptoDay = pto.find((p) => p.date === dateStr) ?? null;

    return { standardRule, override, ptoDay };
  }, [selectedDate, recurring, dateSpecific, pto]);

  // Day editor popover actions
  const handleSaveCustomHours = (data: { startTime: string; endTime: string }) => {
    if (!selectedDate) return;

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const existingOverride = dateSpecific.find((d) => d.date === dateStr);

    if (existingOverride) {
      // Delete existing and create new (pass date, not id)
      deleteeDateAvailabilityMutation.mutate(dateStr, {
        onSuccess: () => {
          createDateAvailabilityMutation.mutate({
            personId,
            date: dateStr,
            startTime: data.startTime,
            endTime: data.endTime,
          });
        },
      });
    } else {
      createDateAvailabilityMutation.mutate({
        personId,
        date: dateStr,
        startTime: data.startTime,
        endTime: data.endTime,
      });
    }

    setSelectedDate(null);
  };

  const handleClearOverride = () => {
    if (!selectedDate) return;

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const existingOverride = dateSpecific.find((d) => d.date === dateStr);

    if (existingOverride) {
      // Pass date, not id (API deletes by date)
      deleteeDateAvailabilityMutation.mutate(dateStr);
    }

    setSelectedDate(null);
  };

  const handleSetPto = () => {
    if (!selectedDate) return;

    createPTOMutation.mutate({
      personId,
      date: format(selectedDate, 'yyyy-MM-dd'),
    });

    setSelectedDate(null);
  };

  const handleClearPto = () => {
    if (!selectedDate) return;

    const dateStr = format(selectedDate, 'yyyy-MM-dd');
    const existingPto = pto.find((p) => p.date === dateStr);

    if (existingPto) {
      deletePTOMutation.mutate(existingPto.id);
    }

    setSelectedDate(null);
  };

  return (
    <div className="bg-background rounded-lg border border-border">
      {/* Header */}
      <div className="p-4 border-b border-border">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-semibold text-foreground">Availability Calendar</h3>
            <p className="text-sm text-foreground-muted">{personName}</p>
          </div>
          <button
            onClick={handleSetStandardHours}
            disabled={isSaving}
            className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-foreground-muted hover:text-foreground border border-border rounded-md hover:bg-background-muted transition-colors disabled:opacity-50"
          >
            <Clock className="h-4 w-4" />
            Set Standard Hours (M-F)
          </button>
        </div>

        {/* Month navigation */}
        <div className="flex items-center justify-between mt-4">
          <div className="flex items-center gap-2">
            <button
              onClick={goToPreviousMonth}
              className="p-1 hover:bg-background-muted rounded transition-colors"
            >
              <ChevronLeft className="h-5 w-5 text-foreground-muted" />
            </button>
            <button
              onClick={goToNextMonth}
              className="p-1 hover:bg-background-muted rounded transition-colors"
            >
              <ChevronRight className="h-5 w-5 text-foreground-muted" />
            </button>
            <button
              onClick={goToToday}
              className="px-2 py-1 text-sm text-foreground-muted hover:text-foreground"
            >
              Today
            </button>
          </div>
          <span className="text-sm font-medium text-foreground">
            {format(currentMonth, 'MMMM yyyy')}
          </span>
        </div>
      </div>

      {/* Standard Week Row */}
      <div className="p-4 border-b border-border">
        <StandardWeekRow
          availability={recurring}
          onEditDay={handleEditStandardDay}
        />

        {/* Standard day edit form */}
        {editingStandardDay !== null && (
          <div className="mt-3 p-3 bg-background-subtle rounded-md border border-border">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-foreground">
                Edit {DAYS_OF_WEEK[editingStandardDay]} Hours
              </span>
              <button
                onClick={() => setEditingStandardDay(null)}
                className="p-1 hover:bg-background-muted rounded transition-colors"
              >
                <X className="h-4 w-4 text-foreground-muted" />
              </button>
            </div>
            <div className="flex flex-wrap items-end gap-3">
              <div>
                <label className="block text-xs text-foreground-muted mb-1">Start</label>
                <input
                  type="time"
                  value={standardDayForm.startTime}
                  onChange={(e) =>
                    setStandardDayForm((prev) => ({ ...prev, startTime: e.target.value }))
                  }
                  className="px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
                />
              </div>
              <div>
                <label className="block text-xs text-foreground-muted mb-1">End</label>
                <input
                  type="time"
                  value={standardDayForm.endTime}
                  onChange={(e) =>
                    setStandardDayForm((prev) => ({ ...prev, endTime: e.target.value }))
                  }
                  className="px-2 py-1.5 text-sm border border-border rounded-md bg-background text-foreground"
                />
              </div>
              <button
                onClick={handleSaveStandardDay}
                disabled={isSaving}
                className="px-3 py-1.5 text-sm font-medium text-primary-foreground bg-primary hover:bg-primary-hover rounded-md transition-colors disabled:opacity-50"
              >
                Save
              </button>
              {getRuleForDayOfWeek(editingStandardDay) && (
                <button
                  onClick={handleDeleteStandardDay}
                  disabled={isSaving}
                  className="px-3 py-1.5 text-sm font-medium text-danger hover:bg-danger-subtle rounded-md border border-danger/30 transition-colors disabled:opacity-50"
                >
                  Set Off
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Month Grid */}
      {isLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      ) : (
        <div className="p-4">
          <MonthGrid
            startDate={gridStartDate}
            weeksToShow={weeksToShow}
            availability={recurring}
            dateOverrides={dateSpecific}
            pto={pto}
            currentMonth={currentMonth}
            onSelectDate={handleSelectDate}
          />
        </div>
      )}

      {/* Legend */}
      <div className="p-3 border-t border-border bg-background-subtle">
        <div className="flex items-center gap-4 text-xs">
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-success" />
            <span className="text-foreground-muted">Working</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-warning" />
            <span className="text-foreground-muted">PTO</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-3 h-3 rounded-full bg-gray-300" />
            <span className="text-foreground-muted">Off</span>
          </div>
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            <span className="text-foreground-muted">Custom hours</span>
          </div>
        </div>
      </div>

      {/* Day Editor Popover */}
      {selectedDate && selectedDateData && (
        <DayEditorPopover
          date={selectedDate}
          standardRule={selectedDateData.standardRule}
          currentOverride={selectedDateData.override}
          currentPto={selectedDateData.ptoDay}
          position={popoverPosition}
          onSaveCustomHours={handleSaveCustomHours}
          onClearOverride={handleClearOverride}
          onSetPto={handleSetPto}
          onClearPto={handleClearPto}
          onClose={() => setSelectedDate(null)}
          isSaving={isSaving}
        />
      )}
    </div>
  );
}
